import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  owner_name: "",
  email: "",
  phone: "",
  property_address: "",
  municipality: "",
  property_size: "",
  road_frontage: "",
  current_use: "",
  near_settlement: "",
  services_nearby: "",
  buildings_on_property: "",
  owns_property: "",
  listed_for_sale: "",
  interest: "",
  description: "",
};

const RadioGroup = ({ name, label, options, value, onChange }) => (
  <fieldset data-testid={`field-${name}`}>
    <legend className="text-xs uppercase tracking-[0.15em] font-semibold text-graphite mb-3">{label}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(name, opt)}
          className={`px-4 py-2.5 text-xs uppercase tracking-[0.1em] border transition-colors duration-300 rounded-none ${
            value === opt ? "bg-ink text-bone border-ink" : "border-ink/20 text-graphite hover:border-ink"
          }`}
          data-testid={`${name}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          {opt}
        </button>
      ))}
    </div>
  </fieldset>
);

const Field = ({ name, label, value, onChange, type = "text", required = false, textarea = false }) => (
  <div data-testid={`field-${name}`}>
    <label htmlFor={name} className="block text-xs uppercase tracking-[0.15em] font-semibold text-graphite mb-3">
      {label} {required && <span className="text-taupe">*</span>}
    </label>
    {textarea ? (
      <textarea id={name} rows={5} className="input-field resize-y" value={value} onChange={(e) => onChange(name, e.target.value)} data-testid={`input-${name}`} />
    ) : (
      <input id={name} type={type} className="input-field" value={value} onChange={(e) => onChange(name, e.target.value)} required={required} data-testid={`input-${name}`} />
    )}
  </div>
);

const PropertyForm = () => {
  const [form, setForm] = useState(initialState);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const update = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.owner_name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      files.forEach((f) => data.append("files", f));
      const res = await axios.post(`${API}/property-submissions`, data);
      setReference(res.data.id);
      toast.success("Your property has been submitted for review.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (reference) {
    return (
      <div className="border border-ink/15 bg-bone p-10 md:p-14 text-center" data-testid="submission-success">
        <span className="inline-flex items-center justify-center w-14 h-14 border border-ink mb-6">
          <Check size={22} />
        </span>
        <h3 className="text-2xl font-semibold uppercase tracking-tight mb-4">Submission Received</h3>
        <p className="text-sm text-graphite leading-relaxed max-w-md mx-auto mb-2">
          Thank you. Stelcore Development will review your property details and respond if the
          opportunity appears suitable.
        </p>
        <p className="text-xs uppercase tracking-[0.15em] text-graphite">
          Reference: <span className="text-ink font-semibold">{reference.slice(0, 8).toUpperCase()}</span>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="border border-ink/15 bg-bone p-8 md:p-12" data-testid="property-submission-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Field name="owner_name" label="Owner Name" value={form.owner_name} onChange={update} required />
        <Field name="email" label="Email" type="email" value={form.email} onChange={update} required />
        <Field name="phone" label="Phone" type="tel" value={form.phone} onChange={update} />
        <Field name="property_address" label="Property Address" value={form.property_address} onChange={update} />
        <Field name="municipality" label="Municipality" value={form.municipality} onChange={update} />
        <Field name="property_size" label="Approximate Property Size / Acreage" value={form.property_size} onChange={update} />
        <Field name="road_frontage" label="Approximate Road Frontage" value={form.road_frontage} onChange={update} />
        <Field name="current_use" label="Current Property Use" value={form.current_use} onChange={update} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
        <RadioGroup name="near_settlement" label="Is the property within or near a town, village or settlement area?" options={["Yes", "No", "Not Sure"]} value={form.near_settlement} onChange={update} />
        <RadioGroup name="services_nearby" label="Are municipal water and sewer nearby?" options={["Yes", "No", "Not Sure"]} value={form.services_nearby} onChange={update} />
        <RadioGroup name="buildings_on_property" label="Is there currently a house or other building on the property?" options={["Yes", "No"]} value={form.buildings_on_property} onChange={update} />
        <RadioGroup name="owns_property" label="Do you currently own the property?" options={["Yes", "No"]} value={form.owns_property} onChange={update} />
        <RadioGroup name="listed_for_sale" label="Is the property currently listed for sale?" options={["Yes", "No"]} value={form.listed_for_sale} onChange={update} />
        <RadioGroup name="interest" label="What are you interested in?" options={["Investing My Property With Stelcore", "Selling My Property", "Exploring Both Options", "Not Sure"]} value={form.interest} onChange={update} />
      </div>

      <div className="mt-10">
        <Field name="description" label="Brief Property Description" value={form.description} onChange={update} textarea />
      </div>

      <div className="mt-10" data-testid="field-documents">
        <label htmlFor="documents" className="block text-xs uppercase tracking-[0.15em] font-semibold text-graphite mb-3">
          Supporting Documents
        </label>
        <label
          htmlFor="documents"
          className="flex flex-col items-center justify-center gap-3 border border-dashed border-ink/25 px-6 py-10 cursor-pointer hover:border-ink transition-colors duration-300"
        >
          <Upload size={20} className="text-graphite" />
          <span className="text-sm text-graphite text-center">
            Survey, property photos, aerial imagery, site plan, planning documents or other relevant files
          </span>
          <span className="text-xs text-graphite/70">PDF, PNG, JPG, WEBP, HEIC, TXT or CSV — up to 8 files, 15 MB each</span>
        </label>
        <input
          id="documents"
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.txt,.csv"
          className="hidden"
          onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 8))}
          data-testid="input-documents"
        />
        {files.length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="selected-files-list">
            {files.map((f) => (
              <li key={f.name} className="text-xs text-graphite flex justify-between border border-ink/10 px-4 py-2">
                <span className="truncate">{f.name}</span>
                <span>{(f.size / 1024 / 1024).toFixed(1)} MB</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-12">
        <button type="submit" disabled={submitting} className="btn-primary w-full md:w-auto disabled:opacity-60" data-testid="submit-property-button">
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Submitting…
            </>
          ) : (
            "Submit Property for Review"
          )}
        </button>
        <p className="text-xs text-graphite leading-relaxed mt-6 max-w-2xl" data-testid="form-disclaimer">
          Submitting a property does not create an obligation, partnership, investment agreement or
          development agreement. All opportunities are subject to Stelcore Development's review, due
          diligence, feasibility, financing, legal documentation and agreement between the parties.
        </p>
      </div>
    </form>
  );
};

export default PropertyForm;
